import React, { useState } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import MainContainer from 'theme/MainConatiner';
import CustomTextField from 'components/Fileds/CustomTextFiled';
import CustomSelect from 'components/Fileds/CustomSelect';
import CustomTextArea from 'components/Fileds/CustomTextArea';
import CustomButton from 'components/Fileds/CustomButton';
import CustomLabelForm from 'components/Fileds/CustomLabelForm';
import { useCreateTicketMutation, useUploadAttachmentsMutation } from './services';
import { useNavigate } from 'react-router-dom';
import { useAutoFocusFirstField } from 'utils/focusHelper';
import './style.css';

export const CATEGORY_OPTIONS = [
  { value: 'Technical Issue', label: 'Technical Issue' },
  { value: 'Order Issue', label: 'Order Issue' },
  { value: 'Payment Issue', label: 'Payment Issue' },
  { value: 'Printer Issue', label: 'Printer Issue' },
  { value: 'Inventory', label: 'Inventory' },
  { value: 'Menu Management', label: 'Menu Management' },
  { value: 'Staff Login', label: 'Staff Login' },
  { value: 'Feature Request', label: 'Feature Request' },
  { value: 'Other', label: 'Other' },
];

export const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const CreateTicketScreen = ({ onBack }) => {
  const navigate = useNavigate();
  const formRef = useAutoFocusFirstField();
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('medium');
  const [description, setDescription] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]); // Store local raw File objects
  const [isDragOver, setIsDragOver] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
    } else {
      navigate('/support-tickets');
    }
  };

  const [createTicket, { isLoading: isCreating }] = useCreateTicketMutation();
  const [uploadAttachments, { isLoading: isUploading }] = useUploadAttachmentsMutation();

  // Form validity check (disables button until required fields are filled)
  const isFormValid = Boolean(
    subject.trim() &&
    category &&
    priority &&
    description.trim()
  );

  // File selection via click
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setSelectedFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  // Drag and Drop Event Handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      setSelectedFiles((prev) => [...prev, ...droppedFiles]);
      e.dataTransfer.clearData();
    }
  };

  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const validate = () => {
    const newErrors = {};
    if (!subject.trim()) newErrors.subject = 'Subject is required';
    if (!category) newErrors.category = 'Category is required';
    if (!priority) newErrors.priority = 'Priority is required';
    if (!description.trim()) newErrors.description = 'Description is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || !isFormValid || isSubmitting || isCreating || isUploading) return;

    setIsSubmitting(true);

    try {
      let uploadedAttachments = [];

      // Step 1: Upload attachments at submission time if local files were selected
      if (selectedFiles.length > 0) {
        const formData = new FormData();
        selectedFiles.forEach((file) => {
          formData.append('files', file);
        });

        const uploadRes = await uploadAttachments(formData).unwrap();
        const uploadedData = uploadRes?.data || [];

        if (Array.isArray(uploadedData)) {
          uploadedAttachments = uploadedData.map((att) => ({
            fileName: att.fileName || att.filename || 'file',
            fileUrl: att.fileUrl || att.url || att.filename,
            fileType: att.fileType || att.type || 'application/octet-stream',
            fileSize: att.fileSize || att.size || 0,
          }));
        } else if (uploadedData) {
          uploadedAttachments = [{
            fileName: uploadedData.fileName || uploadedData.filename || 'file',
            fileUrl: uploadedData.fileUrl || uploadedData.url || uploadedData.filename,
            fileType: uploadedData.fileType || uploadedData.type || 'application/octet-stream',
            fileSize: uploadedData.fileSize || uploadedData.size || 0,
          }];
        }
      }

      // Step 2: Submit ticket creation payload with uploaded attachment filenames
      const payload = {
        subject: subject.trim(),
        category,
        priority,
        description: description.trim(),
        attachments: uploadedAttachments,
      };

      const res = await createTicket(payload).unwrap();
      handleBack();
    } catch (err) {
      console.error('Create ticket submit error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoadingState = isSubmitting || isCreating || isUploading;

  return (
    <MainContainer>
      <Box className="support-tickets-card" sx={{ mb: 4 }}>
        {/* Header with Back Button */}
        <Box className="d-flex align-items-center mb-4" sx={{ gap: 2, pb: 2 }}>
          {/* <IconButton
            onClick={handleBack}
            disabled={isLoadingState}
            sx={{ border: '1px solid #D1D5DB', borderRadius: '50%', width: 36, height: 36 }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton> */}
          <Box className='back-rounded-icon d-flex align-items-center justify-content-center cursor-pointer'
              onClick={handleBack}
              disabled={isLoadingState}
              sx={{ border: '1px solid #4D4D4D', borderRadius: '50%', width: '30px', height: '30px', marginTop: '5px' }}>
              <ChevronLeftIcon />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '22px', color: '#111827' }}>
              Create Support Ticket
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '14px', mt: 0.5 }}>
              Fill in the details below to submit a new support request to our helpdesk.
            </Typography>
          </Box>
        </Box>
        <Divider />

        {/* Form Body */}
        <form ref={formRef} onSubmit={handleSubmit}>
          <Grid container mt={0} spacing={3}>
            {/* Subject */}
            <Grid item xs={12}>
              <CustomLabelForm label="Subject" required />
              <CustomTextField
                name="subject"
                placeholder="Brief summary of the issue (e.g. Printer disconnects during receipt printing)"
                value={subject}
                disabled={isLoadingState}
                onChange={(e) => {
                  setSubject(e.target.value);
                  if (errors.subject) setErrors((prev) => ({ ...prev, subject: '' }));
                }}
                error={Boolean(errors.subject)}
                helperText={errors.subject}
              />
            </Grid>

            {/* Category */}
            <Grid item xs={12} sm={6}>
              <CustomLabelForm label="Category" required />
              <CustomSelect
                name="category"
                placeholder="Select category"
                options={CATEGORY_OPTIONS}
                value={category}
                disabled={isLoadingState}
                onChange={(e) => {
                  setCategory(e.target.value);
                  if (errors.category) setErrors((prev) => ({ ...prev, category: '' }));
                }}
                error={Boolean(errors.category)}
                helperText={errors.category}
              />
            </Grid>

            {/* Priority */}
            <Grid item xs={12} sm={6}>
              <CustomLabelForm label="Priority" required />
              <CustomSelect
                name="priority"
                placeholder="Select priority"
                options={PRIORITY_OPTIONS}
                value={priority}
                disabled={isLoadingState}
                onChange={(e) => {
                  setPriority(e.target.value);
                  if (errors.priority) setErrors((prev) => ({ ...prev, priority: '' }));
                }}
                error={Boolean(errors.priority)}
                helperText={errors.priority}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <CustomLabelForm label="Description" required />
              <CustomTextArea
                name="description"
                placeholder="Provide detailed information about your issue, steps to reproduce, or error messages..."
                minRows={5}
                value={description}
                disabled={isLoadingState}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (errors.description) setErrors((prev) => ({ ...prev, description: '' }));
                }}
                error={Boolean(errors.description)}
                helperText={errors.description}
                style={{ background: 'transparent' }}
              />
            </Grid>

            {/* Attachments Dropzone with Native Drag & Drop */}
            <Grid item xs={12}>
              <CustomLabelForm label="Attachments" />
              <Box
                className={`attachment-upload-box ${isDragOver ? 'drag-over' : ''}`}
                component="label"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  accept="image/*,video/*,.pdf,.doc,.docx,.zip"
                  disabled={isLoadingState}
                />
                <Box display="flex" flexDirection="column" alignItems="center">
                  <CloudUploadIcon sx={{ fontSize: 36, color: isDragOver ? '#000000' : '#6B7280', mb: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111827' }}>
                    {isDragOver ? 'Drop files here' : 'Click to upload files or drag and drop'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6B7280', mt: 0.5 }}>
                    Images, Videos, PDFs, Documents, or ZIP files
                  </Typography>
                </Box>
              </Box>

              {/* Selected Files Preview List */}
              {selectedFiles.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={1.5} mt={2}>
                  {selectedFiles.map((file, idx) => (
                    <Box key={idx} className="attachment-chip">
                      <InsertDriveFileIcon fontSize="small" sx={{ color: '#4B5563' }} />
                      <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500 }}>
                        {file.name} {file.size ? `(${formatFileSize(file.size)})` : ''}
                      </Typography>
                      <IconButton
                        size="small"
                        disabled={isLoadingState}
                        onClick={(e) => {
                          e.preventDefault();
                          handleRemoveFile(idx);
                        }}
                        sx={{ color: '#EF4444', p: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>

            {/* Actions */}
            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <CustomButton
                onClick={handleBack}
                disabled={isLoadingState}
                sx={{
                  width: '120px',
                  backgroundColor: 'transparent',
                  color: '#374151',
                  border: '1px solid #D1D5DB',
                  '&:hover': { backgroundColor: '#F3F4F6' },
                }}
              >
                Cancel
              </CustomButton>

              <CustomButton
                type="submit"
                disabled={!isFormValid || isLoadingState}
                sx={{
                  width: '160px',
                  backgroundColor: !isFormValid || isLoadingState ? '#E5E7EB !important' : '#000000 !important',
                  color: !isFormValid || isLoadingState ? '#9CA3AF !important' : '#ffffff !important',
                  cursor: !isFormValid || isLoadingState ? 'not-allowed !important' : 'pointer !important',
                  '&:hover': {
                    backgroundColor: !isFormValid || isLoadingState ? '#E5E7EB !important' : '#1F2937 !important',
                  },
                }}
              >
                {isLoadingState ? (
                  <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                ) : (
                  'Create Ticket'
                )}
              </CustomButton>
            </Grid>
          </Grid>
        </form>
      </Box>
    </MainContainer>
  );
};

export default CreateTicketScreen;
